import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
import { useTalkFlowDocument } from '@/hooks/apiHooks';
import { isEmptyContent } from '@/utils/editor';
import { stripHTML } from '@/utils';

/** 번역 대상 필드. */
export type TranslatableField = 'subject' | 'summary' | 'content' | 'memo';

export type TranslatedFields = Record<TranslatableField, string>;

export type TranslationSource = {
  /** 번역 API 에 그대로 실을 텍스트 목록 (빈 값 제외) */
  requests: string[];
  /** 응답(같은 순서)을 받아 필드별 텍스트로 되돌린다. 누락분은 원문 유지 */
  rebuild: (translated: (string | undefined)[]) => TranslatedFields;
  /** 번역할 내용이 하나도 없음 */
  isEmpty: boolean;
  /**
   * 제목 말고 실제 읽을 본문(AI 요약 / AI 정리 / 메모)이 있는지.
   * 자동 번역 1회 소진 판정에 쓴다 — 제목만 있는 문서에서 소진되면
   * 이후 AI 정리가 채워져도 자동 번역이 다시 돌지 않는다.
   */
  hasBody: boolean;
  /**
   * 폼 reset(useDocumentData) 이 끝났는지. 하드 리로드 직후에는 content/memo 가 아직
   * 비어 있어 지문이 실제와 다르다. 이 상태로 stale 판정을 하면 "원문이 변경되었어요" 가
   * 잘못 뜨고, 그때 재번역하면 반쪽 원문으로 정상 캐시를 덮어쓴다.
   */
  isHydrated: boolean;
  /**
   * 원문 지문. 번역 시점 값과 비교해 "번역한 뒤 원문이 바뀌었는지" 를 판정한다.
   * 회의록은 AI 정리/AI 요약 재생성·AI 채팅 반영으로 본문이 수시로 갈리기 때문에,
   * 번역본을 그대로 두면 사용자가 옛 내용을 최신인 줄 알고 읽게 된다.
   */
  fingerprint: string;
};

/**
 * 마크다운 줄머리(들여쓰기 + 목록/헤딩/인용 기호)와 본문을 분리하는 패턴.
 * AI 요약은 마크다운이라 줄 단위 구조가 곧 의미인데, 번역 API 는 줄바꿈을 공백으로
 * 합쳐서 돌려준다. 그대로 보내면 "* 항목1 * 항목2" 처럼 기호가 문장에 섞인다.
 * 그래서 줄 단위로 쪼개 본문만 번역하고, 줄머리는 원본을 그대로 다시 붙인다.
 */
const MARKDOWN_LINE = /^(\s*(?:[-*+]\s+|\d+[.)]\s+|#{1,6}\s+|>\s?)?)(.*)$/;

/** 한 줄 = 그대로 둘 줄머리(prefix) + 번역할 본문(body) */
type Line = { prefix: string; body: string };

/** 필드 하나가 차지하는 줄들 (마크다운이 아닌 필드는 1줄) */
type FieldPart = { field: TranslatableField; lines: Line[] };

const toLines = (text: string, splitMarkdown: boolean): Line[] => {
  if (!splitMarkdown) return [{ prefix: '', body: text }];

  return text.split('\n').map((line) => {
    const [, prefix = '', body = ''] = MARKDOWN_LINE.exec(line) ?? [];
    return { prefix, body };
  });
};

/** djb2 — 원문 동일성 판정용 비암호학적 해시 (긴 HTML 을 그대로 들고 비교하지 않기 위함) */
const hash = (input: string): string => {
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 33 + input.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
};

const EMPTY_FIELDS: TranslatedFields = {
  subject: '',
  summary: '',
  content: '',
  memo: '',
};

/**
 * 번역 대상 원문 수집 + 지문 계산.
 * 번역 실행(useRecordTranslation)과 변경 감지(TranslationStatusBar)가
 * 같은 기준을 쓰도록 한곳에 모았다.
 */
const useRecordTranslationSource = (docId: string): TranslationSource => {
  const { watch } = useFormContext<RecordFilter>();
  const { data: documentData } = useTalkFlowDocument(docId);

  // watch 로 구독해야 자동저장/AI 반영으로 값이 바뀔 때 변경 감지가 즉시 재계산된다.
  const formDocId = watch('docId');
  const subject = watch('subject');
  const content = watch('content');
  const memo = watch('memo');
  const isHydrated = Boolean(docId) && formDocId === docId;

  const suggestedSubject = documentData?.suggestedSubject ?? '';
  const commonSummary = documentData?.commonSummary?.text ?? '';

  return useMemo(() => {
    const contentHtml = isEmptyContent(content ?? '') ? '' : content ?? '';
    const memoHtml = isEmptyContent(memo ?? '') ? '' : memo ?? '';
    const summaryText = commonSummary.trim();
    const subjectText = subject?.trim() || suggestedSubject.trim();

    // content/memo 는 Lexical HTML 이라 태그가 구조를 들고 있어 통째로 보내도 안전하다.
    // summary 만 마크다운이라 줄 단위로 쪼갠다.
    const parts: FieldPart[] = (
      [
        ['subject', subjectText, false],
        ['summary', summaryText, true],
        ['content', contentHtml, false],
        ['memo', memoHtml, false],
      ] as [TranslatableField, string, boolean][]
    )
      .filter(([, value]) => Boolean(value))
      .map(([field, value, splitMarkdown]) => ({
        field,
        lines: toLines(value, splitMarkdown),
      }));

    // 실제로 보낼 것은 본문이 있는 줄만 (빈 줄·기호만 있는 줄은 번역할 게 없다)
    const requests: string[] = [];
    parts.forEach(({ lines }) => {
      lines.forEach(({ body }) => {
        if (body.trim()) requests.push(body);
      });
    });

    const rebuild = (translated: (string | undefined)[]): TranslatedFields => {
      const result: TranslatedFields = { ...EMPTY_FIELDS };
      let cursor = 0;

      parts.forEach(({ field, lines }) => {
        result[field] = lines
          .map(({ prefix, body }) => {
            if (!body.trim()) return `${prefix}${body}`;
            // 응답이 비었거나 모자라면 그 줄만 원문 유지 (부분 실패 허용)
            const next = translated[cursor]?.trim() || body;
            cursor += 1;
            return `${prefix}${next}`;
          })
          .join('\n');
      });

      return result;
    };

    // 지문은 태그를 걷어낸 '읽는 글' 기준으로 만든다.
    // Lexical 이 재마운트/정규화하면서 HTML 마크업만 미세하게 달라지는 경우가 있는데,
    // 그걸 원문 변경으로 잘못 잡아 "원문이 변경되었어요" 를 띄우면 안 되기 때문.
    const fingerprintSource = [
      `subject:${subjectText}`,
      `summary:${summaryText}`,
      `content:${stripHTML(contentHtml)}`,
      `memo:${stripHTML(memoHtml)}`,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return {
      requests,
      rebuild,
      isEmpty: requests.length === 0,
      hasBody: Boolean(summaryText || contentHtml || memoHtml),
      isHydrated,
      fingerprint: hash(fingerprintSource),
    };
  }, [subject, suggestedSubject, commonSummary, content, memo, isHydrated]);
};

export default useRecordTranslationSource;
